import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import noteService from "../../services/noteService";
import api from "../api/axios";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import Select from 'react-select';

const CreditNoteForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [formData, setFormData] = useState({
    note_number: "",
    date: new Date().toISOString().split('T')[0],
    customer_id: null,
    sale_id: null,
    amount: 0,
    tax_amount: 0,
    total_amount: 0,
    reason: "",
    status: "completed",
  });

  useEffect(() => {
    fetchCustomers();
    if (id) {
      fetchNote();
    }
  }, [id]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get("/customers/");
      setCustomers(response.data.map(c => ({ value: c.id, label: c.full_name })));
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchSales = async (customerId) => {
    try {
      const response = await api.get(`/sales/?customer_id=${customerId}`);
      setSales(response.data.map(s => ({ value: s.id, label: `Sale #${s.id} - ${new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED' }).format(s.grand_total)}` })));
    } catch (error) {
      console.error("Error fetching sales:", error);
    }
  };

  const fetchNote = async () => {
    try {
      const data = await noteService.getCreditNote(id);
      setFormData({
        ...data,
        date: new Date(data.date).toISOString().split('T')[0]
      });
      if (data.customer_id) fetchSales(data.customer_id);
    } catch (error) {
      console.error("Error fetching note:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };
    
    if (name === "amount" || name === "tax_amount") {
      updatedData.total_amount = parseFloat(updatedData.amount || 0) + parseFloat(updatedData.tax_amount || 0);
    }
    
    setFormData(updatedData);
  };

  const handleSelectChange = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected ? selected.value : null
    }));
    if (name === "customer_id" && selected) {
      fetchSales(selected.value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await noteService.updateCreditNote(id, formData);
      } else {
        await noteService.createCreditNote(formData);
      }
      navigate("/dashboard/credit-notes/list");
    } catch (error) {
      console.error("Error saving credit note:", error);
      const errorMsg = error.response?.data?.detail || "Failed to save credit note.";
      alert(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          background: "none",
          border: "none",
          color: "#4a5568",
          cursor: "pointer",
          marginBottom: "20px",
          fontWeight: "600",
        }}
      >
        <FaArrowLeft /> Back
      </button>

      <div style={{ background: "white", padding: "32px", borderRadius: "16px", boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", marginBottom: "24px" }}>
          {id ? "Edit Credit Note" : "Add New Credit Note"}
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
            <div>
              <label style={labelStyle}>Note Number</label>
              <input
                type="text"
                name="note_number"
                value={formData.note_number}
                onChange={handleChange}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
            <div>
              <label style={labelStyle}>Customer</label>
              <Select
                options={customers}
                value={customers.find(c => c.value === formData.customer_id)}
                onChange={(selected) => handleSelectChange(selected, "customer_id")}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Sale (Optional)</label>
              <Select
                options={sales}
                value={sales.find(s => s.value === formData.sale_id)}
                onChange={(selected) => handleSelectChange(selected, "sale_id")}
                isClearable
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px", marginBottom: "20px" }}>
            <div>
              <label style={labelStyle}>Amount</label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Tax Amount</label>
              <input
                type="number"
                name="tax_amount"
                value={formData.tax_amount}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Total Amount</label>
              <input
                type="number"
                name="total_amount"
                value={formData.total_amount}
                readOnly
                style={{ ...inputStyle, background: "#f7fafc" }}
              />
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Reason / Notes</label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              rows="3"
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              background: "linear-gradient(135deg, #00aaff 0%, #0077cc 100%)",
              color: "white",
              padding: "14px",
              borderRadius: "12px",
              border: "none",
              fontWeight: "700",
              fontSize: "16px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              opacity: loading ? 0.7 : 1,
            }}
          >
            <FaSave /> {loading ? "Saving..." : "Save Credit Note"}
          </button>
        </form>
      </div>
    </div>
  );
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontSize: "14px",
  fontWeight: "600",
  color: "#4a5568",
};

const inputStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "10px",
  border: "1px solid #e2e8f0",
  outline: "none",
  fontSize: "15px",
};

export default CreditNoteForm;
